/**
 * TanStack Query hooks for playbook data (Plan 97/B2).
 */

import { useQuery, type Query } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import { playbookApi } from '../services/api';
import type { PlaybookListItem } from '../types/playbook';

/** Issue #867 — poll the playbook list every 5 s while at least one
 *  playbook is still indexing positions. `ready` / `failed` / `empty` are
 *  all terminal states for the badge (failed needs a clause edit per
 *  #698 §B; the hourly reconciliation cron handles partial → failed
 *  transitions), so polling self-cancels once no playbook is `partial`.
 *  Exported for unit testing; not intended as a public API. */
export function nextPlaybooksRefetchInterval(
  data: PlaybookListItem[] | undefined,
): number | false {
  if (!data || data.length === 0) return false;
  const anyPartial = data.some(
    (pb) => pb.position_embedding_status === 'partial',
  );
  return anyPartial ? 5000 : false;
}

/** Issue #867 — exported as a named const so a test can assert the
 *  refetchInterval is actually wired (not just that the predicate works
 *  in isolation). The hook below is a thin wrapper around this. */
export const playbooksQueryOptions = {
  queryKey: queryKeys.playbooks.all,
  queryFn: () => playbookApi.listPlaybooks(),
  refetchInterval: (query: Query<PlaybookListItem[]>) =>
    nextPlaybooksRefetchInterval(query.state.data),
  // Don't burn the user's battery on a background tab; the next focus
  // + 30 s staleTime expiry will pick up any change anyway.
  refetchIntervalInBackground: false,
} as const;

/** List all user's playbooks.
 *
 *  Issue #867: self-cancelling 5 s poll while any playbook is `partial`.
 *  See playbooksQueryOptions / nextPlaybooksRefetchInterval above. */
export function usePlaybooksQuery() {
  return useQuery(playbooksQueryOptions);
}

/** Get a single playbook with nested clauses, positions, and explanations. */
export function usePlaybookQuery(id: string | null) {
  return useQuery({
    queryKey: queryKeys.playbooks.detail(id!),
    queryFn: () => playbookApi.getPlaybook(id!),
    enabled: !!id,
  });
}

/** List playbooks attached to a matter. */
export function useMatterPlaybooksQuery(matterId: string | null) {
  return useQuery({
    queryKey: queryKeys.playbooks.byMatter(matterId!),
    queryFn: () => playbookApi.listMatterPlaybooks(matterId!),
    enabled: !!matterId,
  });
}

/** List all clause types from taxonomy. */
export function useClauseTypesQuery() {
  return useQuery({
    queryKey: queryKeys.clauseTypes.all,
    queryFn: () => playbookApi.listClauseTypes(),
    staleTime: 1000 * 60 * 60, // 1 hour - taxonomy rarely changes
  });
}

/** Plan 196: bundled fetch for the negotiation right-pane attribution
 * block. Returns position + clause + explanation + playbook in one
 * round trip. Disabled when positionId is null/undefined so unbacked
 * findings don't trigger requests. */
export function usePositionAttributionQuery(positionId: string | null | undefined) {
  return useQuery({
    queryKey: positionId
      ? queryKeys.playbooks.positionAttribution(positionId)
      : ['playbooks', 'position-attribution', 'null'],
    queryFn: () => playbookApi.getPositionAttribution(positionId!),
    enabled: !!positionId,
    staleTime: 5 * 60 * 1000,  // playbook content changes rarely
  });
}
